import { Injectable, computed, inject, signal } from '@angular/core';
import {
  ACTIVE_ASSET_STATUSES,
  ASSET_PHOTO_MAX_COUNT,
  ERR_PIN_122,
  type Asset,
  type AssetPhoto,
  type AssetStatus,
  isAcceptedAssetPhoto,
} from '../../../core/models/asset.model';
import { DEMO_ASSETS, DEMO_MANAGER_TEAM, DEMO_TODAY } from '../../../core/demo/demo-data';
import { requiresSerialNumber } from '../../../core/models/category.model';
import { SessionStore } from '../../../core/state/session.store';
import { APP_CONFIG } from '../../../core/config/app-config.value';
import { InventoryHttpGateway, toApiFailure } from './inventory-http.gateway';
import { assetFromDto, createRequestFromDraft } from './inventory.mapping';

/** Champs saisis dans le formulaire de création d'une fiche matériel (UC-01). */
export interface NewAssetDraft {
  readonly category: string;
  readonly brand: string;
  readonly name: string;
  readonly serialNumber: string | null;
  readonly purchaseDate: string | null;
  readonly warrantyMonths: number | null;
  readonly value: number | null;
  readonly supplier: string | null;
  readonly location: string;
  readonly notes: string | null;
  /** Photos jointes à la création (RM-PIN-122). Le formulaire garantit déjà format et volume. */
  readonly photos: readonly AssetPhoto[];
}

/** Échec de création, porteur du code de la règle violée. */
export interface CreateAssetFailure {
  readonly code: string;
  readonly message: string;
}

/**
 * Ce que la source de données courante sait réellement faire.
 *
 * En mode `demo`, tout est praticable : l'état est local, rien ne peut échouer. En mode `api`, trois
 * parcours n'ont **aucun endpoint** derrière eux, et il vaut mieux le dire à l'utilisateur que lui
 * laisser cliquer sur une action sans effet :
 *
 * - l'affectation existe côté serveur mais exige l'identité du collaborateur, que seul l'annuaire
 *   (SPEC-PIN-008, non branché) peut fournir ;
 * - la vérification de retour (UC-13) et la réforme (UC-05) n'ont pas de use case côté backend —
 *   `Asset.Decommission()` existe dans le domaine, aucune commande ne l'appelle.
 */
export interface InventoryCapabilities {
  readonly canCreate: boolean;
  readonly canReturn: boolean;
  readonly canAssign: boolean;
  readonly canVerifyReturn: boolean;
  readonly canDecommission: boolean;
  /** Raison à afficher quand une action est indisponible. `null` si tout est disponible. */
  readonly unavailableReason: string | null;
}

/**
 * Accès aux données de l'inventaire (SPEC-PIN-001). **Seule porte d'accès** des écrans : ils ne
 * connaissent que ce contrat et ignorent d'où viennent les données.
 *
 * Deux sources, choisies à la compilation (`core/config/app-config.ts`) :
 *
 * - `demo` — le jeu de la maquette v5, en mémoire. L'application tourne sans backend ni base.
 * - `api` — `GET/POST /api/inventory/assets`, servi par `Unipros.Pin.Api`. Ce que ce mode ne sait
 *   pas faire est déclaré dans {@link capabilities} au lieu d'être simulé.
 *
 * Le périmètre par rôle est filtré ici **pour la démonstration seulement**. En production il
 * s'applique côté serveur, avant tout filtre et toute pagination (RM-PIN-195) : `ListAssetsQuery`
 * ne le fait pas encore, et un filtre client n'est pas une mesure de sécurité.
 */
@Injectable({ providedIn: 'root' })
export class InventoryApiService {
  private readonly session = inject(SessionStore);
  private readonly gateway = inject(InventoryHttpGateway);
  private readonly live = APP_CONFIG.dataSource === 'api';

  /** Taille de page maximale acceptée par l'API (`PageRequest.MaxPageSize`). */
  private static readonly MAX_PAGE_SIZE = 100;

  private readonly _assets = signal<readonly Asset[]>(this.live ? [] : DEMO_ASSETS);
  private readonly _loading = signal(false);
  private readonly _loadFailure = signal<CreateAssetFailure | null>(null);
  private readonly _assetsBeyondFirstPage = signal(0);

  constructor() {
    if (this.live) {
      void this.reload();
    }
  }

  readonly all = this._assets.asReadonly();

  /** Chargement en cours : vrai uniquement en mode branché, le temps d'un aller-retour réseau. */
  readonly loading = this._loading.asReadonly();

  /** Dernier échec de lecture, à afficher tel quel : l'API injoignable est le cas courant en local. */
  readonly loadFailure = this._loadFailure.asReadonly();

  /**
   * Nombre de fiches au-delà de la première page (contrat paginé). Zéro tant que le parc tient dans
   * une page (cas de la démo). Exposé pour ne pas tronquer en silence : la pagination d'écran (Lot 4)
   * l'affichera ; d'ici là, une valeur > 0 est annoncée, jamais masquée.
   */
  readonly assetsBeyondFirstPage = this._assetsBeyondFirstPage.asReadonly();

  readonly capabilities: InventoryCapabilities = this.live
    ? {
        canCreate: true,
        canReturn: true,
        canAssign: false,
        canVerifyReturn: false,
        canDecommission: false,
        unavailableReason:
          "Action non disponible en mode branché : l'endpoint correspondant n'existe pas encore côté API.",
      }
    : {
        canCreate: true,
        canReturn: true,
        canAssign: true,
        canVerifyReturn: true,
        canDecommission: true,
        unavailableReason: null,
      };

  /** Parc visible selon le rôle (RM-PIN-195). */
  readonly scoped = computed<readonly Asset[]>(() => {
    const role = this.session.role();
    const assets = this._assets();

    // En mode branché, le détenteur n'est connu que par son identifiant : le rapprocher d'un nom
    // demanderait l'annuaire. Restreindre au hasard serait pire qu'afficher la flotte entière en
    // l'annonçant — le vrai périmètre doit de toute façon être appliqué par le query handler.
    if (this.live) {
      return assets;
    }

    if (role === 'manager') {
      return assets.filter((asset) => asset.holder !== null && DEMO_MANAGER_TEAM.includes(asset.holder));
    }

    if (role === 'collab') {
      return assets.filter((asset) => asset.holder === 'Camille Roussel');
    }

    return assets;
  });

  readonly scopeLabel = computed(() => {
    if (this.live) {
      return 'flotte complète — périmètre par rôle non encore appliqué côté serveur';
    }

    const role = this.session.role();
    if (role === 'manager') return 'équipe (Ventes, Marketing)';
    if (role === 'collab') return 'mon matériel';
    return 'flotte complète';
  });

  /**
   * Recharge le parc depuis l'API. Sans effet en mode démonstration, où l'état local **est** la
   * source : le recharger effacerait les créations de la session.
   */
  async reload(): Promise<void> {
    if (!this.live) {
      return;
    }

    this._loading.set(true);

    try {
      // Une seule page, de taille maximale : le parc alimente des vues agrégées (KPI, périmètre)
      // qui supposent la liste complète. La pagination d'écran viendra au Lot 4 ; d'ici là, tout
      // dépassement d'une page est compté et exposé plutôt que tronqué en silence.
      const parc = await this.gateway.list(null, null, 1, InventoryApiService.MAX_PAGE_SIZE);
      this._assets.set(parc.items.map(assetFromDto));
      this._assetsBeyondFirstPage.set(Math.max(0, parc.total - parc.items.length));
      this._loadFailure.set(null);
    } catch (error) {
      this._assets.set([]);
      this._assetsBeyondFirstPage.set(0);
      this._loadFailure.set(toApiFailure(error, 'Lecture du parc impossible.'));
    } finally {
      this._loading.set(false);
    }
  }

  /** Parc actif : hors réformé et hors perdu/volé (RM-PIN-100). */
  readonly active = computed(() =>
    this.scoped().filter((asset) => ACTIVE_ASSET_STATUSES.includes(asset.status)),
  );

  readonly assigned = computed(() => this.active().filter((asset) => asset.status === 'Assigné'));

  readonly available = computed(() =>
    this.active().filter(
      (asset) => asset.status === 'Disponible' || asset.status === 'Disponible - À vérifier',
    ),
  );

  /** Somme des valeurs d'achat du parc actif (KPI 1, hypothèse H-4 du plan). */
  readonly fleetValue = computed(() =>
    this.active().reduce((total, asset) => total + asset.value, 0),
  );

  /**
   * Taux d'assignation. Quatre segments qui somment à 100 % du parc actif : la spec n'en
   * définissait que deux tout en exigeant un total de 100 %, ce qui laissait « En réparation »
   * et « En commande » hors compte (hypothèse H-5 du plan).
   */
  readonly assignmentRate = computed(() => {
    const active = this.active();
    const total = active.length || 1;
    const share = (status: AssetStatus | AssetStatus[]) => {
      const statuses = Array.isArray(status) ? status : [status];
      return (active.filter((asset) => statuses.includes(asset.status)).length / total) * 100;
    };

    return {
      assigned: share('Assigné'),
      available: share(['Disponible', 'Disponible - À vérifier']),
      repairing: share('En réparation'),
      ordered: share('En commande'),
    };
  });

  /** Jours restants avant la fin de garantie. `null` si la garantie est inconnue. */
  warrantyDays(asset: Asset): number | null {
    const end = parseFrenchDate(asset.warrantyEnd);
    return end ? Math.round((end.getTime() - DEMO_TODAY.getTime()) / 86_400_000) : null;
  }

  /** Matériels dont la garantie expire sous 90 jours, du plus urgent au moins urgent (KPI 3). */
  readonly expiringWarranties = computed(() =>
    this.scoped()
      .map((asset) => ({ asset, days: this.warrantyDays(asset) }))
      .filter((row): row is { asset: Asset; days: number } => row.days !== null && row.days <= 90)
      .sort((a, b) => a.days - b.days),
  );

  byId(id: string): Asset | undefined {
    return this._assets().find((asset) => asset.id === id);
  }

  /** Matériel détenu par un collaborateur, au statut Assigné uniquement (UC-06). */
  heldBy(collaboratorName: string): readonly Asset[] {
    return this._assets().filter(
      (asset) => asset.holder === collaboratorName && asset.status === 'Assigné',
    );
  }

  // --- Création (UC-01) ------------------------------------------------------------

  /**
   * Crée une fiche matériel (UC-01).
   *
   * En mode branché, `POST /api/inventory/assets` fait foi : les contrôles qui suivent ne sont
   * qu'un confort de saisie, et le serveur reste seul juge — c'est lui qui attribue le numéro
   * d'inventaire (RM-PIN-120) et qui rejette un numéro de série en doublon (RM-PIN-001).
   */
  async create(draft: NewAssetDraft): Promise<{ asset: Asset } | { failure: CreateAssetFailure }> {
    // RM-PIN-007 — type, marque et modèle obligatoires pour tout matériel suivi à l'exemplaire.
    const missing = [
      ['type', draft.category],
      ['marque', draft.brand],
      ['modèle', draft.name],
    ]
      .filter(([, value]) => !value?.trim())
      .map(([field]) => field);

    if (missing.length > 0) {
      return {
        failure: {
          code: 'ERR-PIN-007',
          message: `Champs obligatoires manquants : ${missing.join(', ')}.`,
        },
      };
    }

    const serialNumber = draft.serialNumber?.trim() || null;

    // RM-PIN-121 — le numéro de série n'est obligatoire que pour les catégories sérialisées.
    if (requiresSerialNumber(draft.category) && !serialNumber) {
      return {
        failure: {
          code: 'ERR-PIN-121',
          message: `La catégorie « ${draft.category} » est sérialisée : le numéro de série est obligatoire.`,
        },
      };
    }

    // RM-PIN-001 — unicité du numéro de série sur tout le parc, lorsqu'il est renseigné.
    if (serialNumber) {
      const clash = this._assets().find(
        (asset) => asset.serialNumber?.toLowerCase() === serialNumber.toLowerCase(),
      );

      if (clash) {
        return {
          failure: {
            code: 'ERR-PIN-001',
            message: `Le numéro de série ${serialNumber} est déjà porté par ${clash.name} (${clash.inventoryNumber}).`,
          },
        };
      }
    }

    // RM-PIN-122 — format, volume et nombre de photos. Le formulaire écarte déjà les fichiers non
    // conformes ; ce contrôle existe parce qu'une validation qui n'existe qu'en UI n'existe pas.
    const photos = draft.photos;

    if (photos.length > ASSET_PHOTO_MAX_COUNT) {
      return {
        failure: {
          code: 'ERR-PIN-122',
          message: `Une fiche accepte au plus ${ASSET_PHOTO_MAX_COUNT} photos.`,
        },
      };
    }

    if (photos.some((photo) => !isAcceptedAssetPhoto({ type: photo.mimeType, size: photo.sizeBytes }))) {
      return {
        failure: { code: 'ERR-PIN-122', message: ERR_PIN_122 },
      };
    }

    if (this.live) {
      return this.createOnServer(draft, photos);
    }

    const asset: Asset = {
      id: crypto.randomUUID(),
      inventoryNumber: this.nextInventoryNumber(),
      serialNumber,
      brand: draft.brand.trim(),
      name: draft.name.trim(),
      category: draft.category,
      // RM-PIN-019 — création manuelle : le statut initial est « Disponible », aucun autre n'est admis.
      status: 'Disponible',
      holder: null,
      holderId: null,
      holderType: null,
      location: draft.location,
      purchaseDate: draft.purchaseDate ?? '—',
      warrantyMonths: draft.warrantyMonths,
      warrantyEnd: computeWarrantyEnd(draft.purchaseDate, draft.warrantyMonths),
      value: draft.value ?? 0,
      supplier: draft.supplier?.trim() || '—',
      assignedOn: null,
      notes: draft.notes?.trim() || null,
      // Une seule photo principale, même si l'appelant en a marqué plusieurs (RM-PIN-122).
      photos: normalizePhotos(photos),
      history: [
        { date: '24.07.2026', label: 'Fiche créée — statut initial « Disponible »' },
        // Le journal d'audit trace l'ajout comme il tracera la suppression (RM-PIN-122).
        ...(photos.length > 0
          ? [
              {
                date: '24.07.2026',
                label: `${photos.length} photo(s) jointe(s) à la fiche`,
              },
            ]
          : []),
      ],
    };

    this._assets.update((assets) => [asset, ...assets]);

    return { asset };
  }

  /**
   * Création côté serveur, en deux temps : la fiche, puis ses photos.
   *
   * Les photos sont téléversées une par une après coup parce que `POST /assets` ne les accepte pas
   * — RM-PIN-122 est portée par une sous-ressource dédiée. Une photo refusée ne fait donc **pas**
   * échouer la création : la fiche existe, et le message dit ce qui n'a pas suivi.
   */
  private async createOnServer(
    draft: NewAssetDraft,
    photos: readonly AssetPhoto[],
  ): Promise<{ asset: Asset } | { failure: CreateAssetFailure }> {
    let created: { id: string };

    try {
      created = await this.gateway.create(createRequestFromDraft(draft));
    } catch (error) {
      return { failure: toApiFailure(error, 'Création de la fiche refusée.') };
    }

    let photoFailure: CreateAssetFailure | null = null;

    for (const photo of photos) {
      try {
        await this.gateway.attachPhoto(created.id, photo);
      } catch (error) {
        photoFailure = toApiFailure(error, `Photo « ${photo.fileName} » refusée.`);
        break;
      }
    }

    await this.reload();

    const asset = this.byId(created.id);

    if (!asset) {
      return {
        failure: {
          code: 'API-INCOHERENTE',
          message: 'La fiche a été créée mais reste introuvable dans la liste rechargée.',
        },
      };
    }

    return photoFailure ? { failure: photoFailure } : { asset };
  }

  /**
   * Numéro d'inventaire suivant, au format `PIN-AAAA-NNNNN` (RM-PIN-120). Le compteur est propre à
   * l'année et **ne réutilise jamais** un numéro : il repart du plus grand déjà attribué, y compris
   * si la fiche correspondante a été réformée.
   */
  private nextInventoryNumber(): string {
    const year = DEMO_TODAY.getFullYear();
    const prefix = `PIN-${year}-`;

    const highest = this._assets()
      .map((asset) => asset.inventoryNumber)
      .filter((number) => number.startsWith(prefix))
      .map((number) => Number.parseInt(number.slice(prefix.length), 10))
      .reduce((max, value) => (Number.isNaN(value) ? max : Math.max(max, value)), 0);

    return prefix + String(highest + 1).padStart(5, '0');
  }

  // --- Transitions de cycle de vie -------------------------------------------------
  // En mode branché, elles passent par POST /api/inventory/assets/{id}/… et le parc est rechargé :
  // le statut affiché est celui que le domaine a réellement retenu, jamais celui qu'on espérait.
  // En mode démonstration, elles modifient l'état local et journalisent, pour que les parcours de
  // la maquette restent cliquables.

  /**
   * UC-03 — affectation. Seul un matériel « Disponible » peut être affecté (RM-PIN-004).
   *
   * Indisponible en mode branché : `POST /{id}/assignment` attend l'identité du collaborateur, que
   * seul l'annuaire peut fournir — voir {@link capabilities}.
   */
  assign(id: string, holder: string, holderType: 'collab' | 'lieu'): void {
    if (!this.capabilities.canAssign) {
      return;
    }

    this.patch(id, { status: 'Assigné', holder, holderId: null, holderType, assignedOn: '24.07.2026' });
    this.audit(
      id,
      holderType === 'collab'
        ? `Affecté à ${holder} — décharge PDF générée`
        : `Affecté au lieu partagé « ${holder} »`,
    );
  }

  /**
   * UC-04 — restitution. Toute restitution passe par « Disponible - À vérifier » (RM-PIN-016).
   * Renvoie l'échec métier renvoyé par le serveur, ou `null` si la transition a été acceptée.
   */
  async returnAsset(id: string): Promise<CreateAssetFailure | null> {
    if (this.live) {
      try {
        await this.gateway.returnAsset(id);
      } catch (error) {
        return toApiFailure(error, 'Restitution refusée.');
      }

      await this.reload();
      return null;
    }

    const previousHolder = this.byId(id)?.holder ?? 'son détenteur';
    this.patch(id, {
      status: 'Disponible - À vérifier',
      holder: null,
      holderId: null,
      holderType: null,
      assignedOn: null,
    });
    this.audit(id, `Restitué par ${previousHolder} → Disponible - À vérifier`);

    return null;
  }

  /** UC-13 — vérification de retour : conforme → Disponible, défaut → En réparation (RM-PIN-017). */
  verifyReturn(id: string, outcome: 'conforme' | 'defaut'): void {
    if (!this.capabilities.canVerifyReturn) {
      return;
    }

    const status: AssetStatus = outcome === 'conforme' ? 'Disponible' : 'En réparation';
    this.patch(id, { status });
    this.audit(
      id,
      outcome === 'conforme'
        ? 'Vérification de retour conforme → Disponible'
        : 'Vérification de retour : défaut constaté → En réparation',
    );
  }

  /** UC-05 — réforme. État définitif, plus jamais affectable (RM-PIN-006). */
  decommission(id: string): void {
    if (!this.capabilities.canDecommission) {
      return;
    }

    this.patch(id, { status: 'Hors service', holder: null, holderId: null, holderType: null });
    this.audit(id, 'Réformé — sortie définitive du parc');
  }

  private patch(id: string, changes: Partial<Asset>): void {
    this._assets.update((assets) =>
      assets.map((asset) => (asset.id === id ? { ...asset, ...changes } : asset)),
    );
  }

  /** Journal d'audit immuable : on ajoute, on ne modifie jamais (RM-PIN-014). */
  private audit(id: string, label: string): void {
    this._assets.update((assets) =>
      assets.map((asset) =>
        asset.id === id
          ? { ...asset, history: [...asset.history, { date: '24.07.2026', label }] }
          : asset,
      ),
    );
  }
}

/**
 * RM-PIN-122 — exactement une photo principale : la première marquée comme telle, à défaut la
 * première de la liste. Sans cela, deux photos principales rendraient l'affichage en liste et
 * l'étiquette d'inventaire non déterministes.
 */
function normalizePhotos(photos: readonly AssetPhoto[]): readonly AssetPhoto[] {
  if (photos.length === 0) {
    return [];
  }

  const primaryIndex = Math.max(
    0,
    photos.findIndex((photo) => photo.isPrimary),
  );

  return photos.map((photo, index) => ({ ...photo, isPrimary: index === primaryIndex }));
}

/** Les dates de la maquette sont au format `JJ.MM.AAAA`. */
export function parseFrenchDate(value: string | null): Date | null {
  if (!value || !value.includes('.')) {
    return null;
  }

  const [day, month, year] = value.split('.').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * RM-PIN-008 — la fin de garantie est **calculée** à partir de la date d'achat et de la durée en
 * mois. Elle n'est jamais saisie directement, sinon les deux valeurs peuvent diverger.
 */
export function computeWarrantyEnd(
  purchaseDate: string | null,
  warrantyMonths: number | null,
): string | null {
  const start = parseFrenchDate(purchaseDate);
  if (!start || !warrantyMonths || warrantyMonths <= 0) {
    return null;
  }

  const end = new Date(start);
  end.setMonth(end.getMonth() + warrantyMonths);

  // `setMonth` reporte au mois suivant quand le jour n'existe pas (31 janvier + 1 mois).
  // Le dernier jour du mois cible est plus fidèle à l'intention d'une durée de garantie.
  if (end.getDate() !== start.getDate()) {
    end.setDate(0);
  }

  const pad = (value: number) => String(value).padStart(2, '0');
  return `${pad(end.getDate())}.${pad(end.getMonth() + 1)}.${end.getFullYear()}`;
}
